import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@volca/ui/lib/toast";
import { useApi } from "@/core/api";
import { reportKey } from "@/profitability/query";
import { reportsPath } from "@/profitability/report-query";
import { settingsKey } from "@/profitability/settings/query";
import { reportError } from "@/profitability/utils";

/** Someone of the business a template can be shared with. */
export interface EligibleViewer {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

/** A manager a template is shared with (`ProfitabilityReportViewer`). */
export interface TemplateViewer {
  id: string;
  user: EligibleViewer;
  email_on_review: boolean;
  granted_by: { id: string; full_name: string } | null;
  created_at: string;
}

interface ViewersResponse {
  viewers: TemplateViewer[];
  eligible_users: EligibleViewer[];
}

const viewersKey = (businessId: string, reportId: string) => [
  ...reportKey(businessId),
  "viewers",
  reportId,
];

const viewersPath = (businessId: string, reportId: string, id?: string) =>
  `${reportsPath(businessId, reportId)}/viewers${id ? `/${id}` : ""}`;

/** Who the template is shared with, and who it could be. Finance only. */
export const useTemplateViewers = (
  businessId: string,
  reportId: string,
  enabled = true,
) => {
  const api = useApi();
  return useQuery({
    queryKey: viewersKey(businessId, reportId),
    queryFn: ({ signal }) =>
      api
        .get<ViewersResponse>(viewersPath(businessId, reportId), { signal })
        .then((res) => res.data),
    enabled,
    retry: false,
  });
};

/**
 * Shares, updates and unshares the template, reloading after each the list,
 * the settings (whose owners are picked from who it is shared with) and the
 * template.
 */
export const useViewerMutations = (businessId: string, reportId: string) => {
  const api = useApi();
  const client = useQueryClient();
  const reload = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: viewersKey(businessId, reportId) }),
      client.invalidateQueries({ queryKey: settingsKey(businessId, reportId) }),
      client.invalidateQueries({
        queryKey: [...reportKey(businessId), "reports"],
      }),
    ]);
  const failed = (title: string) => (error: unknown) =>
    toast.error(`${title}. Try again.`, { description: reportError(error) });
  const add = useMutation({
    mutationFn: (userId: string) =>
      api.post(viewersPath(businessId, reportId), { user_id: userId }),
    onSuccess: reload,
    onError: failed("They could not be given access"),
  });
  const setEmail = useMutation({
    mutationFn: ({ id, email }: { id: string; email: boolean }) =>
      api.patch(viewersPath(businessId, reportId, id), {
        email_on_review: email,
      }),
    onSuccess: reload,
    onError: failed("The email setting could not be saved"),
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete(viewersPath(businessId, reportId, id)),
    onSuccess: reload,
    onError: failed("Their access could not be removed"),
  });
  return { add, setEmail, remove };
};
